import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import apiService from './services/api';
import { useNavigationTracker } from './hooks/navigationHistory';

// Social Platform Pages
import Feed from './pages/Feed';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Upload from './pages/Upload';
import Explore from './pages/Explore';
import CommunitiesHub from './pages/CommunitiesHub';
import CommunityWizard from './pages/CommunityWizard';
// Community.jsx contains channel builder component used by CommunityWizard
import Notifications from './pages/Notifications';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Messages from './pages/Messages';
import Wallet from './pages/Wallet';
import SkillTree from './pages/SkillTree';
import AIModels from './pages/AIModels';
import Studio from './pages/Studio';
import LiveStreamPage, { LiveStreamsBrowse } from './pages/LiveStreamPage';
import TrendsPage from './pages/TrendsPage';
import CreatorAnalyticsDashboard from './pages/CreatorAnalyticsDashboard';
import ArtStudio from './pages/ArtStudio';
import WritersStudio from './pages/WritersStudio';
import Pricing from './pages/Pricing';
import VideoBoost from './pages/VideoBoost';

// AI Analysis Tools (preserved from original)
// TODO: Re-enable when components are created
// import { VideoManager } from './components/VideoManager';
// import { BufferDashboard } from './components/BufferDashboard';
// import { NodeClusterStatus } from './components/NodeClusterStatus';

/**
 * AIIA - TikTok + Discord Hybrid Platform with AI Video Analysis
 * Main feed: Short-form video feed with engagement tracking
 * Communities: Discord-style spaces for creators and subscribers
 * AI Tools: Video authenticity analysis accessible from creator dashboard
 */
export default function App() {
  const { user, loading, isAuthenticated, logout, authNotice, clearAuthNotice } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useNavigationTracker();
  
  const [hasNotifications, setHasNotifications] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');

  const handleLogout = useCallback(() => {
    Promise.resolve(logout()).catch((error) => {
      console.warn('Logout failed:', error);
    });
  }, [logout]);

  useEffect(() => {
    let isMounted = true;
    let pollId = null;

    const pollNotifications = async () => {
      if (!isAuthenticated) {
        if (isMounted) {
          setHasNotifications(false);
        }
        return;
      }

      try {
        const data = await apiService.getNotifications({ limit: 1, offset: 0, unread_only: true });
        if (isMounted) {
          const unreadCount = Array.isArray(data?.notifications) ? data.notifications.length : 0;
          setHasNotifications(unreadCount > 0);
        }
      } catch (error) {
        console.warn('Notification poll failed:', error.message);
      }
    };

    pollNotifications();
    pollId = setInterval(pollNotifications, 60000);

    return () => {
      isMounted = false;
      if (pollId) {
        clearInterval(pollId);
      }
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (location.pathname !== '/explore') return;
    const params = new URLSearchParams(location.search);
    setGlobalSearch(params.get('q') || '');
  }, [location.pathname, location.search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950">
        <div className="text-white text-xl">Loading AIIA...</div>
      </div>
    );
  }

  const noticeStyles = {
    error: 'bg-red-500/20 border-red-500/40 text-red-100',
    warning: 'bg-amber-400/20 border-amber-400/40 text-amber-100',
    info: 'bg-blue-500/15 border-blue-500/30 text-blue-100'
  };

  // Public routes that don't require auth
  const publicRoutes = ['/', '/feed', '/login', '/signup', '/explore'];
  const isPublicRoute = publicRoutes.includes(location.pathname);
  
  // Only redirect to login for protected routes
  if (!isAuthenticated && !isPublicRoute && !location.pathname.startsWith('/profile/')) {
    navigate('/login');
    return null;
  }

  const profileInitial = user?.displayName?.[0]?.toUpperCase() || user?.username?.[0]?.toUpperCase() || 'U';
  const isImmersivePage = ['/', '/feed', '/upload'].includes(location.pathname);

  return (
    <div className="app h-screen w-full bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white overflow-hidden">
      {authNotice && (
        <div className="fixed top-16 left-0 right-0 z-30 px-4 sm:px-6 lg:px-8 pt-2 pointer-events-none">
          <div
            className={`pointer-events-auto max-w-4xl mx-auto flex items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-sm shadow-lg shadow-black/20 backdrop-blur-md ${
              noticeStyles[authNotice.type] || 'bg-indigo-500/20 border-indigo-500/40 text-indigo-100'
            }`}
          >
            <div>
              <p className="font-semibold">{authNotice.message}</p>
              <p className="text-xs text-white/70">You can log back in from the feed at any time.</p>
            </div>
            <button
              onClick={clearAuthNotice}
              className="text-xs uppercase tracking-wide text-white/70 hover:text-white transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <header className="fixed top-0 left-0 right-0 z-40 border-b border-white/10 bg-slate-950/85 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
          <Link
            to="/notifications"
            className="relative w-12 h-12 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center text-2xl hover:border-white/30 transition"
            aria-label="Alerts"
          >
            🔔
            {hasNotifications && <span className="absolute top-2 right-3 w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
          </Link>

          <Link
            to="/explore"
            className="w-12 h-12 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center text-2xl hover:border-white/30 transition"
            aria-label="Explore"
          >
            🧭
          </Link>

          <form onSubmit={(event) => {
            event.preventDefault();
            const query = globalSearch.trim();
            if (query) {
              navigate(`/explore?q=${encodeURIComponent(query)}`);
            } else {
              navigate('/explore');
            }
          }} className="flex-1 flex items-center gap-2">
            <input
              type="search"
              value={globalSearch}
              onChange={(event) => setGlobalSearch(event.target.value)}
              placeholder="Search creators, topics, or findings"
              className="flex-1 rounded-2xl bg-black/40 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
            />
            <button
              type="submit"
              className="px-4 py-3 rounded-2xl bg-gradient-to-r from-purple-500 to-emerald-400 text-sm font-semibold text-white"
            >
              Search
            </button>
          </form>

          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <>
                <Link
                  to={`/profile/${user?.username || 'me'}`}
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center font-bold"
                  aria-label="Profile"
                >
                  {profileInitial}
                </Link>
                <button
                  onClick={handleLogout}
                  className="hidden sm:inline-flex px-4 py-2 rounded-full border border-white/10 text-xs font-semibold text-white/80 hover:text-white hover:border-white/40"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-semibold text-white/80 hover:text-white"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-sm font-semibold"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Bottom Navigation Bar - Keep it simple */}
      <nav className="fixed bottom-0 left-0 right-0 w-full z-50 border-t border-white/10 bg-slate-950/95 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-5 gap-2 py-3">
            {[
              { to: '/feed', icon: '🏠', label: 'Home' },
              { to: '/communities', icon: '👥', label: 'Groups' },
              { to: '/upload', icon: '➕', label: 'Create' },
              { to: '/messages', icon: '💬', label: 'Inbox' },
              { to: '/skilltree', icon: '🏆', label: 'Rewards' },
            ].map((item) => (
              <NavLink key={item.to} {...item} />
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className={`h-full w-full ${isImmersivePage ? 'overflow-hidden' : 'pt-20 pb-28 overflow-y-auto'}`}>
        <Routes>
          {/* Social Platform Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/upload" element={<Upload />} />
          <Route path="/profile/:username" element={<Profile />} />
          <Route path="/communities" element={<CommunitiesHub />} />
          <Route path="/communities/create" element={<CommunityWizard />} />
          <Route path="/communities/:communityId" element={<CommunitiesHub />} />
          <Route path="/communities/:communityId/:channelSlug" element={<CommunitiesHub />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/studio" element={<Studio />} />
          <Route path="/skilltree" element={<SkillTree />} />
          <Route path="/models" element={<AIModels />} />
          <Route path="/live" element={<LiveStreamsBrowse />} />
          <Route path="/live/:streamId" element={<LiveStreamPage />} />
          <Route path="/trends" element={<TrendsPage />} />
          <Route path="/analytics" element={<CreatorAnalyticsDashboard />} />
          <Route path="/art-studio" element={<ArtStudio />} />
          <Route path="/stories/write" element={<WritersStudio />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/subscribe" element={<Pricing />} />
          <Route path="/video-boost" element={<VideoBoost />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          {/* AI Analysis Tools (Creator-only) - TODO: Re-enable when components exist */}
          {/* <Route path="/tools/analysis" element={<ToolsLayout><VideoManager /></ToolsLayout>} /> */}
          {/* <Route path="/tools/nodes" element={<ToolsLayout><NodeClusterStatus /></ToolsLayout>} /> */}
          {/* <Route path="/tools/buffer" element={<ToolsLayout><BufferDashboard /></ToolsLayout>} /> */}

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </div>
  );
}

// Helper components
function NavLink({ to, icon, label, badge = false }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link
      to={to}
      className={`relative flex flex-col items-center justify-center gap-1 rounded-2xl py-2 text-xs font-semibold border transition ${
        isActive
          ? 'text-white bg-white/10 border-white/20'
          : 'text-slate-300 border-transparent hover:text-white hover:border-white/20'
      }`}
      aria-label={label}
      title={label}
    >
      <span className="text-2xl">{icon}</span>
      <span>{label}</span>
      {badge && <span className="absolute top-2 right-4 w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
    </Link>
  );
}

function NotFound() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] text-center px-4">
      <h1 className="text-6xl font-bold mb-4">404</h1>
      <p className="text-xl text-slate-300 mb-8">Page not found</p>
      <button
        onClick={() => navigate('/')}
        className="px-6 py-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 font-semibold"
      >
        Go Home
      </button>
    </div>
  );
}
